import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Carousel } from 'react-bootstrap';

const ProductMediaModal = ({
  show,
  onHide,
  mediaItems,
  initialIndex,
  title,
  resolveMediaUrl,
  isVideo
}) => {
  const items = useMemo(() => (Array.isArray(mediaItems) ? mediaItems : []), [mediaItems]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  const hasMultipleItems = items.length > 1;

  const goToNext = () => {
    if (!hasMultipleItems) {
      return;
    }
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  const goToPrev = () => {
    if (!hasMultipleItems) {
      return;
    }
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handleTouchStart = (event) => {
    const point = event.touches?.[0];
    if (!point) {
      return;
    }
    setTouchStartX(point.clientX);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX == null || !hasMultipleItems) {
      setTouchStartX(null);
      return;
    }

    const point = event.changedTouches?.[0];
    if (!point) {
      setTouchStartX(null);
      return;
    }

    const deltaX = point.clientX - touchStartX;
    const swipeThreshold = 40;

    if (deltaX <= -swipeThreshold) {
      goToNext();
    } else if (deltaX >= swipeThreshold) {
      goToPrev();
    }

    setTouchStartX(null);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    const maxIndex = Math.max(items.length - 1, 0);
    const nextIndex = Number.isInteger(initialIndex) ? Math.min(Math.max(initialIndex, 0), maxIndex) : 0;
    setActiveIndex(nextIndex);
  }, [show, initialIndex, items.length]);

  if (!show || items.length === 0) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="product-media-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Carousel
          activeIndex={activeIndex}
          onSelect={(selectedIndex) => setActiveIndex(selectedIndex)}
          controls={hasMultipleItems}
          indicators={false}
          interval={null}
          slide={false}
          touch
          ride={undefined}
        >
          {items.map((media, index) => {
            const mediaUrl = resolveMediaUrl(media);
            return (
              <Carousel.Item key={index}>
                <div
                  className="product-media-modal-stage"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  {isVideo(media) ? (
                    <video className="product-media-modal-video" controls preload="metadata" playsInline>
                      <source src={mediaUrl} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      className="product-media-modal-image"
                      src={mediaUrl}
                      alt={`${title} - ${index + 1}`}
                    />
                  )}
                </div>
              </Carousel.Item>
            );
          })}
        </Carousel>
        {hasMultipleItems && (
          <div className="product-media-modal-indicator-wrap">
            <div className="product-media-modal-counter">{activeIndex + 1} / {items.length}</div>
            <div className="product-media-modal-dots" role="tablist" aria-label="Media position">
              {items.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  type="button"
                  className={`product-media-modal-dot${index === activeIndex ? ' is-active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to media ${index + 1}`}
                  aria-current={index === activeIndex}
                />
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProductMediaModal;